import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Confirmed via Swagger (curl in the working request): base includes /api/dashboard
const NOTIF_BASE = 'https://car.amar.co.jp/api/dashboard/global-notification';
const LIST_URL      = `${NOTIF_BASE}/get-all-list`;
const READ_BY_URL    = (id) => `${NOTIF_BASE}/read-by/${id}`;
const READ_ALL_URL   = `${NOTIF_BASE}/read-all`;
const DELETE_BY_URL  = (id) => `${NOTIF_BASE}/delete-by/${id}`;
// Backend only exposes "clear-read" (deletes read notifications), there's no
// true "clear-all" endpoint — Clear All button is bound to this.
const CLEAR_READ_URL = `${NOTIF_BASE}/clear-read`;

// Backend now filters notifications by the caller's role, so every request
// needs the admin's token — it used to work token-less because the old
// global topic/list had no per-admin filtering to do.
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminAuthToken');
  return {
    'accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/* ── Exact icon/color mapping per the GlobalNotificationType enum ── */
const TYPE_META = {
  NEW_USER_REGISTRATION:  { icon: '👤', bg: '#ede9fe', color: '#7c3aed' },
  NEW_ADMIN_REGISTRATION: { icon: '👤', bg: '#ede9fe', color: '#7c3aed' },
  NEW_INQUIRY:            { icon: '📬', bg: '#dbeafe', color: '#2563eb' },
  NEW_INQUIRY_TICKET:     { icon: '🎫', bg: '#fef3c7', color: '#d97706' },
  NEW_ORDER_TICKET:       { icon: '🎫', bg: '#fef3c7', color: '#d97706' },
  TICKET_CLOSED:          { icon: '🎫', bg: '#dcfce7', color: '#16a34a' },
  TICKET_REOPENED:        { icon: '🎫', bg: '#fee2e2', color: '#dc2626' },
  NEW_CHAT_MESSAGE:       { icon: '💬', bg: '#e0e7ff', color: '#4f46e5' },
  NEW_REVIEW:             { icon: '⭐', bg: '#fef9c3', color: '#ca8a04' },
  REVIEW_UPDATED:         { icon: '⭐', bg: '#fef9c3', color: '#ca8a04' },
  ORDER_PLACED:           { icon: '📦', bg: '#fee2e2', color: '#dc2626' },
};
const DEFAULT_META = { icon: '⚙️', bg: '#e0e7ff', color: '#4f46e5' };

const getMetaForType = (globalNotificationtype) => TYPE_META[globalNotificationtype] || DEFAULT_META;

// Frontend owns routing — backend only tells us the type + referenceId.
const routeFor = (n) => {
  switch (n.type) {
    case 'NEW_USER_REGISTRATION':
    case 'NEW_ADMIN_REGISTRATION': return '/adminDashBoard/users/allUsers';
    case 'NEW_INQUIRY':            return `/adminDashBoard/inquiries/AllInquiries`;
    case 'NEW_INQUIRY_TICKET':
    case 'NEW_ORDER_TICKET':
    case 'TICKET_CLOSED':
    case 'TICKET_REOPENED':
    case 'NEW_CHAT_MESSAGE':       return `/adminDashBoard/tickets/allTickets`;
    case 'NEW_REVIEW':
    case 'REVIEW_UPDATED':         return `/adminDashBoard/reviews`;
    case 'ORDER_PLACED':           return `/adminDashBoard/orders/allOrders`;
    default:                       return '/adminDashBoard/dashboard';
  }
};

// Which Tickets tab (inquiry vs order) allTickets.js should open on — it
// already supports this via `location.state.tab` ("inquiry" | "order").
// NEW_INQUIRY_TICKET / NEW_ORDER_TICKET tell us directly; the other 3
// ticket-related types (TICKET_CLOSED, TICKET_REOPENED, NEW_CHAT_MESSAGE)
// don't say which kind, so fall back to the ticket number's prefix
// (IRT- = inquiry, ORT- = order — same convention used throughout the app).
const getTicketsTab = (n) => {
  if (n.type === 'NEW_ORDER_TICKET') return 'order';
  if (n.type === 'NEW_INQUIRY_TICKET') return 'inquiry';
  const ref = (n.referenceId || '').toString().toUpperCase();
  if (ref.startsWith('ORT')) return 'order';
  return 'inquiry';
};

// Backend sends createdAt as "DD-MM-YYYY HH:mm:ss" — not natively parseable
// by `new Date(...)`, so parse it manually.
const parseCreatedAt = (createdAt) => {
  if (!createdAt) return null;
  const [datePart, timePart = '00:00:00'] = createdAt.split(' ');
  const [dd, mm, yyyy] = (datePart || '').split('-').map(Number);
  const [hh, min, ss] = timePart.split(':').map(Number);
  if (!dd || !mm || !yyyy) return null;
  // Backend time is UTC (no timezone in string) — build it as UTC so the browser converts to local (IST)
  return new Date(Date.UTC(yyyy, mm - 1, dd, hh || 0, min || 0, ss || 0));
};

const timeAgo = (date) => {
  if (!date) return '';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString('en-GB');
};

/* All styles are plain JS objects applied inline — this guarantees the
   layout renders correctly even if an external stylesheet fails to load
   or gets overridden by other CSS in the project. */
const S = {
  container: {
    padding: '28px 30px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f1f5f9',
    minHeight: '100vh',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    gap: 16,
    flexWrap: 'wrap',
  },
  headerLeft: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    marginTop: 2,
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  titleBar: {
    display: 'block',
    width: 36,
    height: 3,
    background: '#e11d48',
    borderRadius: 2,
    marginTop: 6,
  },
  subtitle: { fontSize: '0.82rem', color: '#64748b', margin: '8px 0 0' },
  actions: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  actionBtn: (disabled) => ({
    background: '#fff',
    color: '#e11d48',
    border: '1.5px solid #e11d48',
    padding: '7px 16px',
    borderRadius: 20,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 500,
    fontSize: '0.83rem',
    opacity: disabled ? 0.4 : 1,
    whiteSpace: 'nowrap',
  }),
  actionBtnOutline: (disabled) => ({
    background: '#fff',
    color: '#64748b',
    border: '1.5px solid #cbd5e1',
    padding: '7px 16px',
    borderRadius: 20,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 500,
    fontSize: '0.83rem',
    opacity: disabled ? 0.4 : 1,
    whiteSpace: 'nowrap',
  }),
  tabs: { display: 'flex', gap: 8, marginBottom: 18 },
  tab: (active) => ({
    background: active ? '#e11d48' : '#fff',
    border: `1px solid ${active ? '#e11d48' : '#e2e8f0'}`,
    color: active ? '#fff' : '#475569',
    padding: '7px 18px',
    borderRadius: 20,
    fontSize: '0.83rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  }),
  tabBadge: (active) => ({
    background: active ? 'rgba(255,255,255,0.25)' : '#fee2e2',
    color: active ? '#fff' : '#e11d48',
    fontSize: '0.7rem',
    fontWeight: 700,
    borderRadius: 10,
    padding: '1px 7px',
    minWidth: 18,
    textAlign: 'center',
  }),
  list: {
    background: '#ffffff',
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  item: (unread, hovered, borderBottom) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '16px 20px',
    borderBottom: borderBottom,
    cursor: 'pointer',
    position: 'relative',
    background: hovered ? (unread ? '#fef1f1' : '#f8fafc') : (unread ? '#fff7f7' : '#fff'),
    borderLeft: unread ? '3px solid #e11d48' : '3px solid transparent',
  }),
  itemIcon: (bg, color) => ({
    width: 42,
    height: 42,
    minWidth: 42,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.15rem',
    flexShrink: 0,
    background: bg,
    color: color,
  }),
  itemBody: { flex: 1, minWidth: 0 },
  itemTop: { display: 'flex', alignItems: 'center', gap: 8 },
  itemTitle: { fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' },
  unreadDot: { width: 8, height: 8, borderRadius: '50%', background: '#e11d48', flexShrink: 0 },
  countPill: {
    fontSize: '0.68rem',
    fontWeight: 700,
    color: '#4f46e5',
    background: '#e0e7ff',
    borderRadius: 10,
    padding: '1px 7px',
    flexShrink: 0,
  },
  itemMessage: { margin: '4px 0 6px', fontSize: '0.83rem', color: '#64748b', lineHeight: 1.5 },
  itemTime: { fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 },
  deleteBtn: (hovered) => ({
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: hovered ? 1 : 0,
    transition: 'opacity 0.15s, background 0.15s, color 0.15s',
    flexShrink: 0,
    marginLeft: 'auto',
    alignSelf: 'center',
  }),
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '70px 20px',
    textAlign: 'center',
  },
  emptyIcon: { fontSize: '2.4rem', marginBottom: 14, opacity: 0.5 },
  emptyTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: '0 0 4px' },
  emptySub: { fontSize: '0.82rem', color: '#94a3b8', margin: 0 },
  centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '70px 20px', gap: 10 },
};

// Same DTO shape used for the initial list fetch AND the live WS push from
// AdminDashboard's /topic/admin-notifications subscription — shared here so
// both paths map fields identically.
const mapNotification = (n) => ({
  id: n.globalNotificationId,
  type: n.globalNotificationtype,
  referenceType: n.referenceType,
  referenceId: n.referenceId,
  title: n.title,
  message: n.message,
  actorName: n.actorName,
  messageCount: n.messageCount || 1,
  read: n.read === true,
  createdAtDate: parseCreatedAt(n.createdAt),
});

const NotificationsPage = ({ onBack, onUnreadCountChange, liveNotification, onNavigateToInquiry, onNavigateToOrder }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(LIST_URL, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to load notifications');
        const data = await response.json();

        const mapped = (Array.isArray(data) ? data : []).map(mapNotification);

        // Newest first
        mapped.sort((a, b) => (b.createdAtDate?.getTime() || 0) - (a.createdAtDate?.getTime() || 0));

        setNotifications(mapped);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  // Keep the list live — same push AdminDashboard already uses for the bell
  // badge, upserted by id so a folded update (same id, newer text) replaces
  // in place instead of showing as a duplicate row.
  useEffect(() => {
    if (!liveNotification) return;
    const mapped = mapNotification(liveNotification);
    setNotifications(prev => {
      const idx = prev.findIndex(n => n.id === mapped.id);
      if (idx === -1) return [mapped, ...prev];
      const next = [...prev];
      next[idx] = mapped;
      return next;
    });
  }, [liveNotification]);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (onUnreadCountChange) onUnreadCountChange(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const visibleNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  // "Read is shared" per backend — marking one/all read reflects for every
  // admin, not just this session. Optimistic update, revert on failure.
  const markAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      const res = await fetch(READ_BY_URL(id), { method: 'PUT', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('mark-read failed');
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: false } : n));
    }
  };

  const markAllAsRead = async () => {
    const previous = notifications;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      const res = await fetch(READ_ALL_URL, { method: 'PUT', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('read-all failed');
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setNotifications(previous);
    }
  };

  // Single delete by ID — DELETE /global-notification/delete-by/{id}
  const deleteNotification = async (id) => {
    const ok = window.confirm('Delete this notification? This cannot be undone.');
    if (!ok) return;

    const previous = notifications;
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      const res = await fetch(DELETE_BY_URL(id), { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('delete-by failed');
    } catch (err) {
      console.error('Error deleting notification:', err);
      setNotifications(previous);
      window.alert('Could not delete the notification. Please try again.');
    }
  };

  // Clear all — DELETE /global-notification/clear-read
  // Backend only clears READ notifications (returns { deleted: N }), so
  // after success we drop read notifications locally and keep unread ones.
  const clearAll = async () => {
    const ok = window.confirm('Clear all read notifications? This cannot be undone.');
    if (!ok) return;

    const previous = notifications;
    setNotifications(prev => prev.filter(n => !n.read));
    try {
      const res = await fetch(CLEAR_READ_URL, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('clear-read failed');
    } catch (err) {
      console.error('Error clearing notifications:', err);
      setNotifications(previous);
      window.alert('Could not clear notifications. Please try again.');
    }
  };

  const handleItemClick = (n) => {
    if (!n.read) markAsRead(n.id);

    // Inquiries/Orders already have a working "navigate + auto-open the
    // specific one" mechanism (used elsewhere, e.g. converting an inquiry
    // to an order) — reuse it instead of building a second one here.
    if (n.type === 'NEW_INQUIRY' && onNavigateToInquiry) {
      onNavigateToInquiry(n.referenceId);
      return;
    }
    if (n.type === 'ORDER_PLACED' && onNavigateToOrder) {
      onNavigateToOrder(n.referenceId);
      return;
    }

    const route = routeFor(n);
    const isTicketsRoute = route === '/adminDashBoard/tickets/allTickets';
    const isReviewsRoute = route === '/adminDashBoard/reviews';
    const isUsersRoute = route === '/adminDashBoard/users/allUsers';
    let state;
    if (isTicketsRoute) state = { tab: getTicketsTab(n), autoOpenTicketNumber: n.referenceId };
    else if (isReviewsRoute) state = { autoOpenReviewId: n.referenceId };
    else if (isUsersRoute) state = { autoOpenUserId: n.referenceId };
    navigate(route, state ? { state } : undefined);
  };

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          {onBack && (
            <button style={S.backBtn} onClick={onBack} aria-label="Back to dashboard">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          <div>
            <h2 style={S.title}>
              Notifications
              <span style={S.titleBar}></span>
            </h2>
            <p style={S.subtitle}>{notifications.length} total &middot; {unreadCount} unread</p>
          </div>
        </div>

        <div style={S.actions}>
          {/* <button style={S.actionBtn(unreadCount === 0)} onClick={markAllAsRead} disabled={unreadCount === 0}>
            Mark all as read
          </button> */}
          <button style={S.actionBtn(notifications.length === 0)} onClick={clearAll} disabled={notifications.length === 0}>
            Clear all read
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={S.tabs}>
        <button style={S.tab(filter === 'all')} onClick={() => setFilter('all')}>
          All
        </button>
        <button style={S.tab(filter === 'unread')} onClick={() => setFilter('unread')}>
          Unread {unreadCount > 0 && <span style={S.tabBadge(filter === 'unread')}>{unreadCount}</span>}
        </button>
      </div>

      {/* List */}
      <div style={S.list}>
        {loading ? (
          <div style={S.centerState}>
            <p style={S.emptyTitle}>Loading notifications…</p>
          </div>
        ) : error ? (
          <div style={S.centerState}>
            <div style={S.emptyIcon}>⚠️</div>
            <p style={S.emptyTitle}>Couldn't load notifications</p>
            <p style={S.emptySub}>{error}</p>
          </div>
        ) : visibleNotifications.length === 0 ? (
          <div style={S.empty}>
            <div style={S.emptyIcon}>🔔</div>
            <p style={S.emptyTitle}>{filter === 'unread' ? "You're all caught up" : 'No notifications'}</p>
            <p style={S.emptySub}>
              {filter === 'unread' ? 'New notifications will show up here.' : 'Notifications you receive will appear in this list.'}
            </p>
          </div>
        ) : (
          visibleNotifications.map((n, idx) => {
            const meta = getMetaForType(n.type);
            const isLast = idx === visibleNotifications.length - 1;
            return (
              <div
                key={n.id}
                style={S.item(!n.read, hoveredId === n.id, isLast ? 'none' : '1px solid #f1f5f9')}
                onClick={() => handleItemClick(n)}
                onMouseEnter={() => setHoveredId(n.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div style={S.itemIcon(meta.bg, meta.color)}>
                  <span>{meta.icon}</span>
                </div>
                <div style={S.itemBody}>
                  <div style={S.itemTop}>
                    <span style={S.itemTitle}>{n.title}</span>
                    {n.messageCount > 1 && !n.read && <span style={S.countPill}>{n.messageCount} messages</span>}
                    {!n.read && <span style={S.unreadDot}></span>}
                  </div>
                  <p style={S.itemMessage}>{n.message}</p>
                  <span style={S.itemTime}>{timeAgo(n.createdAtDate)}</span>
                </div>
                <button
                  style={S.deleteBtn(hoveredId === n.id)}
                  onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                  aria-label="Delete notification"
                  title="Delete notification"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v13a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10zM10 11v6M14 11v6"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;